import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaEye, FaComments, FaClock, FaCheckCircle } from 'react-icons/fa';
import { useSubscriptionCheck } from '../../components/SubscriptionGate';

export default function MyRequests() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const { checkAdLimit, incrementAdCount } = useSubscriptionCheck();
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [formData, setFormData] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    request_type: 'product',
    category_id: '',
    quantity: '',
    unit: '',
    deadline: '',
    budget: '',
    location: '',
    images: []
  });
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    if (user) {
      fetchRequests();
      fetchCategories();
    }
  }, [user]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests/my-requests?user_id=${user.id}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = {
        ...formData,
        quantity: formData.quantity ? parseInt(formData.quantity) : null,
        budget: formData.budget ? parseFloat(formData.budget) : null
      };

      if (editRequest) {
        await axios.put(
          `${API}/requests/update/${editRequest.id}?user_id=${user.id}`,
          data
        );
        toast.success(i18n.language === 'fr' ? 'Demande mise à jour' : 'Request updated');
      } else {
        await axios.post(
          `${API}/requests/create?user_id=${user.id}`,
          data
        );
        // Increment subscription ad count for new request
        await incrementAdCount('request');
        toast.success(i18n.language === 'fr' ? 'Demande créée' : 'Request created');
      }

      setDialogOpen(false);
      resetForm();
      fetchRequests();
    } catch (error) {
      console.error('Error saving request:', error);
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' 
        ? 'Erreur lors de l\'enregistrement' 
        : 'Error saving request'));
    }
  };

  const handleEdit = (request) => {
    setEditRequest(request);
    setFormData({
      title_fr: request.title_fr,
      title_en: request.title_en,
      description_fr: request.description_fr,
      description_en: request.description_en,
      request_type: request.request_type,
      category_id: request.category_id || '',
      quantity: request.quantity || '',
      unit: request.unit || '',
      deadline: request.deadline || '',
      budget: request.budget || '',
      location: request.location || '',
      images: request.images || []
    });
    setImageUrl('');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(i18n.language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette demande ?' 
      : 'Are you sure you want to delete this request?')) {
      try {
        await axios.delete(`${API}/requests/delete/${id}?user_id=${user.id}`);
        toast.success(i18n.language === 'fr' ? 'Demande supprimée' : 'Request deleted');
        fetchRequests();
      } catch (error) {
        console.error('Error deleting request:', error);
        toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting request');
      }
    }
  };

  const handleAddImage = () => {
    if (imageUrl && !formData.images.includes(imageUrl)) {
      setFormData({...formData, images: [...formData.images, imageUrl]});
      setImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({...formData, images: newImages});
  };

  const resetForm = () => {
    setEditRequest(null);
    setFormData({
      title_fr: '',
      title_en: '',
      description_fr: '',
      description_en: '',
      request_type: 'product',
      category_id: '',
      quantity: '',
      unit: '',
      deadline: '',
      budget: '',
      location: '',
      images: []
    });
    setImageUrl('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="px-3 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-sm flex items-center gap-1">
            <FaCheckCircle /> {i18n.language === 'fr' ? 'Actif' : 'Active'}
          </span>
        );
      case 'closed':
        return (
          <span className="px-3 py-1 bg-gray-200 text-gray-700 rounded-full text-sm">
            {i18n.language === 'fr' ? 'Fermé' : 'Closed'}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540]">
            {i18n.language === 'fr' ? 'Mes Demandes' : 'My Requests'}
          </h1>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={async () => { 
                // Check subscription limit
                const check = await checkAdLimit('request');
                if (!check.allowed) {
                  toast.error(check.message || 'Limite d\'annonces atteinte', {
                    description: 'Passez à un plan supérieur pour créer plus de demandes',
                    action: { label: 'Voir les offres', onClick: () => navigate('/pricing') }
                  });
                  return;
                }
                resetForm(); 
                setDialogOpen(true); 
              }} className="bg-[#635BFF]">
                <FaPlus className="mr-2" />
                {i18n.language === 'fr' ? 'Nouvelle Demande' : 'New Request'}
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editRequest 
                    ? (i18n.language === 'fr' ? 'Modifier la demande' : 'Edit Request')
                    : (i18n.language === 'fr' ? 'Nouvelle demande' : 'New Request')}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Titre (FR) *</Label>
                    <Input 
                      required 
                      value={formData.title_fr} 
                      onChange={(e) => setFormData({...formData, title_fr: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Title (EN) *</Label>
                    <Input 
                      required 
                      value={formData.title_en} 
                      onChange={(e) => setFormData({...formData, title_en: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Description (FR) *</Label>
                    <textarea
                      required
                      value={formData.description_fr}
                      onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                      className="w-full min-h-[80px] p-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <Label>Description (EN) *</Label>
                    <textarea
                      required
                      value={formData.description_en}
                      onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                      className="w-full min-h-[80px] p-2 border rounded-md"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Type *</Label>
                    <Select value={formData.request_type} onValueChange={(value) => setFormData({...formData, request_type: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="product">Produit</SelectItem>
                        <SelectItem value="service">Service</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Catégorie</Label>
                    <Select value={formData.category_id} onValueChange={(value) => setFormData({...formData, category_id: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Quantité</Label>
                    <Input 
                      type="number" 
                      value={formData.quantity} 
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label>Unité</Label>
                    <Input 
                      value={formData.unit} 
                      onChange={(e) => setFormData({...formData, unit: e.target.value})}
                      placeholder="ex: pcs, m³"
                    />
                  </div>

                  <div>
                    <Label>Budget (€)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={formData.budget} 
                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Délai souhaité</Label>
                    <Input 
                      value={formData.deadline} 
                      onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                      placeholder="ex: 2 semaines, urgent"
                    />
                  </div>

                  <div>
                    <Label>Localisation</Label>
                    <Input 
                      value={formData.location} 
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="ex: Paris, France"
                    />
                  </div>
                </div>

                {/* Images */}
                <div>
                  <Label>Images (URL)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={imageUrl} 
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://example.com/image.jpg"
                    />
                    <Button type="button" onClick={handleAddImage} variant="outline">
                      {i18n.language === 'fr' ? 'Ajouter' : 'Add'}
                    </Button>
                  </div>
                  {formData.images.length > 0 && (
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt="" className="w-full h-20 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button type="submit" className="bg-[#0A2540]">
                    {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Requests List */}
        {requests.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">
              {i18n.language === 'fr' 
                ? 'Vous n\'avez pas encore de demandes' 
                : 'You don\'t have any requests yet'}
            </p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#635BFF]">
              <FaPlus className="mr-2" />
              {i18n.language === 'fr' ? 'Créer ma première demande' : 'Create my first request'}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {requests.map(request => (
              <Card key={request.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg">
                      {i18n.language === 'fr' ? request.title_fr : request.title_en}
                    </h3>
                    {getStatusBadge(request.status)}
                  </div>

                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {i18n.language === 'fr' ? request.description_fr : request.description_en}
                  </p>

                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <FaEye /> {request.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaComments /> {request.responses_count || 0}
                    </span>
                    <span>{new Date(request.created_at).toLocaleDateString()}</span>
                  </div>

                  {request.deadline && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                      <FaClock className="text-[#00D4FF]" />
                      <span>{request.deadline}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleEdit(request)} 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                    >
                      <FaEdit className="mr-1" /> {i18n.language === 'fr' ? 'Modifier' : 'Edit'}
                    </Button>
                    <Button 
                      onClick={() => handleDelete(request.id)} 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}